import Layout from '@comp/layout'
import SolutionMeta from '@comp/solution/solution-meta'
import SolutionHero from '@comp/solution/solution-hero'
import SolutionPillars, { Pillar } from '@comp/solution/solution-pillars'
import ProductHowItWorks, { Step } from '@comp/product/product-how-it-works'
import SolutionRelated, { RelatedItem } from '@comp/solution/solution-related'
import FinalCta from '@comp/home/final-cta'

const pillars: Pillar[] = [
  {
    title: 'One graph for every identity',
    description:
      'Directory users, SaaS entitlements from connected apps, federated sign-ins, service accounts, AI agents, all correlated into one canonical identity, with confidence you can inspect.',
    bullets: [
      'Human, machine, and agent identities on one graph',
      'Automatic correlation with per-link confidence scores',
      'Manual link / unlink / re-assign when you know better',
      'Interactive Identity Explorer: list and graph views',
    ],
  },
  {
    title: 'Effective access and blast radius',
    description:
      'Not what someone was granted, but what they can actually reach, resolved through every group, role, and permission chain. And the reverse: who can reach this resource.',
    bullets: [
      'Effective access with the exact “via” path shown',
      'Blast-radius analysis for incident response',
      'Change-impact preview before you touch a role',
      'Scope-aware edges: an “Owner” of what, exactly',
    ],
  },
  {
    title: 'Findings that get fixed, not filed',
    description:
      'Most visibility tools hand you a list and wish you luck. Monosign owns the control plane, so a finding is one click away from remediation, with a dry run first and an undo after.',
    bullets: [
      'Orphans, dormant-but-entitled, SoD violations, ownerless agents',
      'One-click revoke: single edge, whole account, or bulk',
      'Dry-run preview and what-if simulation before action',
      'Undo restores what the remediation removed',
    ],
  },
  {
    title: 'Ask it in plain language',
    description:
      'From “accounts not linked to any identity” to “most-entitled accounts in GitLab”: natural-language questions become deterministic graph queries you can save, schedule, and alert on.',
    bullets: [
      'Natural-language queries over the identity graph',
      'Saved queries with threshold alerts',
      'Hand any result set to Access Certification',
      'CSV and scheduled report export for audit evidence',
    ],
  },
]

const steps: Step[] = [
  {
    title: 'Correlate',
    description:
      'Sources flow into the graph and accounts snap to canonical identities; updates land in seconds, at 50k+ user scale.',
  },
  {
    title: 'Explore',
    description:
      'Trace effective access and blast radius; watch posture KPIs: uncorrelated, dormant, MFA gaps, risky paths.',
  },
  {
    title: 'Remediate',
    description:
      'Work the findings queue with dry-run, bulk actions, and undo, or let saved queries alert you when drift returns.',
  },
]

const related: RelatedItem[] = [
  {
    label: 'Use case: identity security posture',
    description:
      'How the graph, the score, and ITDR work as one program.',
    href: '/solutions/identity-security-posture',
  },
  {
    label: 'Blog: what is IVIP?',
    description:
      'The identity category behind this page, explained.',
    href: '/blog/what-is-identity-visibility-and-intelligence-ivip',
  },
  {
    label: 'What is IVIP?',
    description:
      'Identity visibility and intelligence, explained in the glossary.',
    href: '/glossary/identity-visibility-and-intelligence',
  },
]

export default function IdentityIntelligencePage() {
  return (
    <Layout>
      <SolutionMeta
        title="Identity visibility & intelligence (IVIP)"
        description="A unified identity graph across directory, SaaS, federated, and AI-agent identities: effective access with paths, blast-radius analysis, posture findings with one-click remediation and undo, and natural-language queries."
        path="/sign/identity-intelligence"
      />
      <main className="bg-surface text-ink-primary">
        <SolutionHero
          eyebrow="Monosign · Identity Intelligence (IVIP)"
          title={
            <>
              See who can access what.
              <br />
              Then fix it, same screen.
            </>
          }
          body="An Identity Visibility and Intelligence Platform (IVIP) inside your IdP: Monosign correlates every identity you connect (directory, SaaS, federated, human, and machine) into one graph that answers effective access and blast radius, surfaces posture findings, and remediates them with a dry run and an undo."
          badges={[
            'IVIP: Gartner’s newest identity category',
            'Effective access + blast radius',
            'Sees and fixes, not just reports',
          ]}
        />
        <SolutionPillars
          title={<>Most IVIP tools report. This one acts.</>}
          body="Identity visibility and intelligence platforms are usually read-only overlays that export findings to a ticket queue. Monosign is the IdP and the IGA: the same platform that sees the problem holds the lever that fixes it."
          pillars={pillars}
        />
        <ProductHowItWorks
          title={<>Correlate, explore, remediate.</>}
          steps={steps}
        />
        <SolutionRelated items={related} />
        <FinalCta />
      </main>
    </Layout>
  )
}
